import { createPortal } from "react-dom";
import AffiliateEarnSection from "./AffiliateEarnSection";

/**
 * The member's sharing links, in a popup of their own.
 *
 * The same section that sits in the profile, given a modal so a member can
 * find it where they look for account things — beside Profile, Change
 * password and My Plans — rather than only by scrolling their profile.
 *
 * A popup is the right shape for THIS: links are fetched, copied and
 * forgotten. The earnings they produce are a different job and have their own
 * page (AffiliateEarningsPage); `onSeeEarnings` is the door between them.
 *
 * Rendered through a PORTAL to document.body, which is not optional here: this
 * is opened from the account menu, which lives inside the header, and the
 * header shell carries `backdrop-blur-md`. A backdrop-filter makes an element
 * a containing block for `position: fixed` descendants, so without the portal
 * the overlay is trapped inside the header bar — it drew as a thin strip
 * across the top of the page instead of covering it.
 */
export default function AffiliateEarnModal({ token, memberToken, primary = "#0274F7", onClose, onChanged, onSeeEarnings }) {
  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-stretch justify-center bg-ink/60 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="flex max-h-full w-full flex-col overflow-hidden bg-surface sm:max-w-lg sm:rounded-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-5 py-4 sm:px-7">
          <h2 className="text-base font-bold text-gray-900">My sharing links</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 sm:px-7">
          {/* `bare` because the modal already supplies the heading and the
              card around it — the section would otherwise draw a second one. */}
          <AffiliateEarnSection
            token={token}
            memberToken={memberToken}
            primary={primary}
            bare
            onChanged={onChanged}
            onSeeEarnings={onSeeEarnings}
          />
        </div>
      </div>
    </div>,
    document.body,
  );
}
